import React from 'react';
import { Form, Input, Button, InputNumber, Card, Row, Col, Divider, Collapse, theme, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Product } from '../../api/productApi';
import { useTranslation } from 'react-i18next';

const units = ['piece', 'set', 'carton', 'meter', 'roll', 'box', 'other'];

type ProductFormProps = {
    initialValues?: Product;
    onFinish: (values: Product) => void;
    loading?: boolean;
};

const ProductForm: React.FC<ProductFormProps> = ({ initialValues, onFinish, loading }) => {
    const { token } = theme.useToken();
    const { t } = useTranslation();

    return (
        <Form
            layout="vertical"
            initialValues={initialValues || { currentStock: [] }}
            onFinish={onFinish}
            style={{
                maxWidth: 1200,
                margin: '0 auto',
                padding: 16,
                background: token.colorBgLayout,
                borderRadius: 18,
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
            }}
        >
            <Card
                title={<span style={{ color: token.colorPrimary, fontWeight: 700 }}>{t('product_information')}</span>}
                style={{
                    marginBottom: 20,
                    borderRadius: 12,
                    background: token.colorBgContainer,
                    boxShadow: '0 1px 8px rgba(56, 104, 255, 0.05)'
                }}
                headStyle={{ borderBottom: `1px solid ${token.colorPrimaryBg}` }}
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
                            <Input autoFocus placeholder={t('product_code') as string} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
                            <Input placeholder={t('product_name') as string} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item name="category" label={t('category')}>
                            <Input placeholder={t('category') as string} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="sku" label={t('sku')}>
                            <Input placeholder={t('sku') as string} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="barcode" label={t('barcode')}>
                            <Input placeholder={t('barcode') as string} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={6}>
                        <Form.Item name="buyPriceInDollar" label={t('buy_price_dollar')} rules={[{ required: true }]}>
                            <InputNumber min={0} style={{ width: '100%' }} placeholder={t('buy_price_dollar') as string} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item name="buyPriceInDinar" label={t('buy_price_iqd')} rules={[{ required: true }]}>
                            <InputNumber min={0} style={{ width: '100%' }} placeholder={t('buy_price_iqd') as string} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item name="sellPriceInDollar" label={t('sell_price_dollar')} rules={[{ required: true }]}>
                            <InputNumber min={0} style={{ width: '100%' }} placeholder={t('sell_price_dollar') as string} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item name="sellPriceInDinar" label={t('sell_price_iqd')} rules={[{ required: true }]}>
                            <InputNumber min={0} style={{ width: '100%' }} placeholder={t('sell_price_iqd') as string} />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            <Divider style={{
                borderColor: token.colorPrimary,
                borderWidth: 2,
                borderRadius: 2,
                margin: '24px 0'
            }} />

            <Collapse
                defaultActiveKey={['stock']}
                ghost
                style={{
                    background: token.colorBgContainer,
                    borderRadius: 10,
                    marginBottom: 16,
                    boxShadow: '0 1px 6px rgba(0,0,0,0.03)'
                }}
                items={[
                    {
                        key: 'stock',
                        label: (
                            <span style={{
                                fontWeight: 700,
                                color: token.colorPrimary
                            }}>
                                {t('current_stock')}
                                <Button
                                    type="dashed"
                                    onClick={e => {
                                        e.stopPropagation();
                                        // @ts-ignore
                                        document.getElementById('add-stock-btn')?.click();
                                    }}
                                    size="small"
                                    icon={<PlusOutlined />}
                                    style={{
                                        marginLeft: 16,
                                        background: token.colorPrimaryBg,
                                        borderColor: token.colorPrimary
                                    }}
                                >
                                    {t('add_stock_entry')}
                                </Button>
                            </span>
                        ),
                        children: (
                            <Form.List name="currentStock">
                                {(fields, { add, remove }) => (
                                    <>
                                        <Row gutter={8} style={{ fontWeight: 500, marginBottom: 4 }}>
                                            <Col span={4}>{t('unit')}</Col>
                                            <Col span={4}>{t('qty')}</Col>
                                            <Col span={6}>{t('unit_length')}</Col>
                                            <Col span={6}>{t('date')}</Col>
                                            <Col span={2}></Col>
                                        </Row>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Row
                                                gutter={8}
                                                key={key}
                                                style={{
                                                    marginBottom: 8,
                                                    background: key % 2 === 0 ? token.colorBgLayout : token.colorBgContainer,
                                                    borderRadius: 6,
                                                    padding: 4
                                                }}
                                            >
                                                <Col span={4}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'unit']}
                                                        rules={[{ required: true }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Select
                                                            options={units.map(u => ({ value: u, label: t(u) }))}
                                                            placeholder={t('unit')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={4}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'qty']}
                                                        rules={[{ required: true }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <InputNumber min={0} placeholder={t('qty') as string} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={6}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'unitLength']}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <InputNumber min={0} placeholder={t('unit_length') as string} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={6}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'date']}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Input type="date" style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Button
                                                        icon={<MinusCircleOutlined />}
                                                        type="link"
                                                        danger
                                                        onClick={() => remove(name)}
                                                    />
                                                </Col>
                                            </Row>
                                        ))}
                                        <Button
                                            id="add-stock-btn"
                                            type="dashed"
                                            block
                                            icon={<PlusOutlined />}
                                            onClick={() => add()}
                                            style={{
                                                marginTop: 4,
                                                background: token.colorPrimaryBg,
                                                borderColor: token.colorPrimary
                                            }}
                                        >
                                            {t('add_another_stock_entry')}
                                        </Button>
                                    </>
                                )}
                            </Form.List>
                        )
                    }
                ]}
            />

            <Divider style={{ margin: '24px 0', borderColor: token.colorPrimary, borderWidth: 2 }} />

            <Form.Item style={{ textAlign: 'center' }}>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{
                        minWidth: 140,
                        borderRadius: 8,
                        fontWeight: 600,
                        background: token.colorPrimary,
                        borderColor: token.colorPrimary
                    }}
                >
                    {initialValues ? t('update') : t('create')}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default ProductForm;